import { idbGet, idbSet } from '@/lib/cache';
import { simplifyFeatureCollection } from '@/lib/geoUtils';
import { getMemorySnapshot } from '@/lib/memoryGuard';
import { computeBbox } from '@/lib/worldMap/mapUtils';
import { pipGeometry } from '@/lib/geoUtils';
import { cleanFeatures, REGION_OVERRIDES } from './worldData/splitParts';
export { cleanFeatures } from './worldData/splitParts';
export { unwrapRegionsAntimeridian } from './worldData/antimeridian';

const GEOJSON_URL =
  'https://raw.githubusercontent.com/johan/world.geo.json/master/countries.geo.json';
const REGION_DATA_URL =
  'https://cdn.jsdelivr.net/gh/lukes/ISO-3166-Countries-with-Regional-Codes@master/all/all.json';
// Natural Earth 10m admin-0 — used as the sole country source because it
// includes all sovereign states and territories (Cape Verde, Comoros, many
// Caribbean & Pacific island nations, …) that the johan dataset omits.
const NE_GEO_URL =
  'https://cdn.jsdelivr.net/gh/nvkelso/natural-earth-vector@master/geojson/ne_10m_admin_0_countries.geojson';
const NE_GEO_URL_FALLBACK =
  'https://raw.githubusercontent.com/nvkelso/natural-earth-vector/master/geojson/ne_10m_admin_0_countries.geojson';
const NE_GEO_OVERVIEW_URL =
  'https://cdn.jsdelivr.net/gh/nvkelso/natural-earth-vector@master/geojson/ne_50m_admin_0_countries.geojson';
export const WORLD_COUNTRY_COUNT = 257;
export const WORLD_ISLAND_COUNT = 3998;

async function fetchJsonWithTimeout(url, timeoutMs = 8000) {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeoutMs);
  try {
    const response = await fetch(url, {
      signal: controller.signal,
      headers: { Accept: 'application/json' },
    });
    if (!response.ok) {
      throw new Error(`HTTP ${response.status}`);
    }
    return await response.json();
  } finally {
    clearTimeout(timeoutId);
  }
}

let neRefreshing = false;

function processAdmin0(data) {
  return data.features
    .filter((f) => {
      const code = f.properties.ADM0_A3 || f.properties.ISO_A3;
      return code && code !== '-99' && f.geometry;
    })
    .map((f) => ({
      type: 'Feature',
      id: f.properties.ADM0_A3 || f.properties.ISO_A3,
      properties: {
        name: f.properties.ADMIN || f.properties.NAME || f.properties.NAME_LONG,
      },
      geometry: f.geometry,
    }));
}

async function fetchAdmin0FromNetwork() {
  const candidates = [NE_GEO_URL, NE_GEO_URL_FALLBACK, GEOJSON_URL];
  let lastError;

  for (const url of candidates) {
    try {
      const data = await fetchJsonWithTimeout(url, 8000);
      if (url === GEOJSON_URL) return data.features || [];
      return processAdmin0(data);
    } catch (error) {
      lastError = error;
    }
  }

  throw lastError || new Error('Could not load country dataset');
}

function isValidFeatureList(value) {
  return Array.isArray(value) && value.length > 0 && value.some((feature) => (
    feature && feature.geometry && feature.properties
  ));
}

function refreshAdmin0() {
  if (neRefreshing || getMemorySnapshot().low) return;
  neRefreshing = true;
  fetchAdmin0FromNetwork()
    .then((features) => {
      idbSet('ne_admin0_v3', features);
    })
    .catch(() => {})
    .finally(() => { neRefreshing = false; });
}

// Load the Natural Earth 10m admin-0 dataset as the sole country source — it
// includes all sovereign states and territories (Cape Verde, Comoros, many
// Caribbean & Pacific island nations, …) that the johan dataset omits. Johan
// is kept only as a fallback if NE is unreachable. Results are cached in
// IndexedDB (stale-while-revalidate) so repeat visits render instantly.
let neFeaturesPromise = null;

function ensureAdmin0() {
  if (neFeaturesPromise) return neFeaturesPromise;
  neFeaturesPromise = (async () => {
    const cached = await idbGet('ne_admin0_v3');
    if (isValidFeatureList(cached)) {
      refreshAdmin0();
      return cached;
    }
    const features = await fetchAdmin0FromNetwork();
    if (!isValidFeatureList(features)) throw new Error('Country dataset was empty');
    await idbSet('ne_admin0_v3', features);
    return features;
  })();
  return neFeaturesPromise;
}

// Cleaned (processed) features cache — skips the expensive splitParts +
// unwrapAntimeridian processing on repeat visits. The raw GeoJSON is cached
// separately (ne_admin0_v3); this stores the post-cleanFeatures result so
// the map renders near-instantly without re-processing thousands of polygons.
let cleanedPromise = null;
let overviewPromise = null;

function cleanFeaturesInWorker(urls = [NE_GEO_URL, NE_GEO_URL_FALLBACK, GEOJSON_URL], options = {}) {
  return new Promise((resolve, reject) => {
    const worker = new Worker(new URL('./worldData/cleaner.worker.js', import.meta.url), { type: 'module' });
    const timeoutId = setTimeout(() => finish(reject, new Error('Country data processing timed out')), 45_000);
    const finish = (callback, value) => {
      clearTimeout(timeoutId);
      worker.terminate();
      callback(value);
    };
    worker.addEventListener('message', ({ data }) => {
      if (data.error) finish(reject, new Error(data.error));
      else finish(resolve, data.features);
    }, { once: true });
    worker.addEventListener('error', (event) => {
      finish(reject, event.error || new Error(event.message || 'Country data worker failed'));
    }, { once: true });
    worker.postMessage({ urls, ...options });
  });
}

function ensureCleaned() {
  if (cleanedPromise) return cleanedPromise;
  cleanedPromise = (async () => {
    try {
      const cached = await idbGet('ne_admin0_cleaned_v5');
      if (isValidFeatureList(cached)) {
        // Synchronous flag so App.jsx can skip the loading screen entirely
        // for returning users — the map renders from IDB in ~50ms.
        try { localStorage.setItem('geo_cache_ready', '1'); } catch {}
        return cached;
      }
      const processed = await cleanFeaturesInWorker().catch(async () => {
        const raw = await ensureAdmin0();
        try { return cleanFeatures(raw); }
        finally { neFeaturesPromise = null; }
      });
      if (!isValidFeatureList(processed)) throw new Error('Processed country dataset was empty');
      try { localStorage.setItem('geo_cache_ready', '1'); } catch {}
      // Defer the cache write so the structured clone of the large GeoJSON
      // doesn't block the initial map render.
      const writeCache = () => idbSet('ne_admin0_cleaned_v5', processed);
      if (typeof requestIdleCallback === 'function') {
        requestIdleCallback(writeCache);
      } else {
        setTimeout(writeCache, 500);
      }
      return processed;
    } catch (error) {
      // Do not cache an empty result or mark the cache ready after a failed
      // load. A later retry must be able to recover from transient network,
      // IndexedDB, or browser WebView failures.
      try { localStorage.removeItem('geo_cache_ready'); } catch {}
      cleanedPromise = null;
      throw error;
    }
  })();
  return cleanedPromise;
}

export async function loadCountryFeatures() {
  return ensureCleaned();
}

export function loadOverviewCountryFeatures(tolerance = 0.08) {
  if (overviewPromise) return overviewPromise;
  overviewPromise = (async () => {
    const cacheKey = `ne_admin0_overview_v4_${Math.round(tolerance * 1000)}`;
    const cached = await idbGet(cacheKey);
    if (isValidFeatureList(cached)) return { features: cached, detailed: false };

    let features;
    try {
      features = await cleanFeaturesInWorker([NE_GEO_OVERVIEW_URL], { tolerance });
    } catch {
      const data = await fetchJsonWithTimeout(NE_GEO_OVERVIEW_URL);
      features = simplifyFeatureCollection({
        type: 'FeatureCollection',
        features: cleanFeatures(processAdmin0(data)),
      }, tolerance).features;
    }
    if (!isValidFeatureList(features)) throw new Error('Overview country dataset was empty');
    const writeCache = () => idbSet(cacheKey, features);
    if (typeof requestIdleCallback === 'function') requestIdleCallback(writeCache);
    else setTimeout(writeCache, 500);
    return { features, detailed: false };
  })().catch((error) => {
    overviewPromise = null;
    throw error;
  });
  return overviewPromise;
}

// Build lookups from the split feature set:
// - codeToCountry: feature code → parent country code
// - partsByCountry: country code → all its part codes (mainland + islands)
// - countryToName: country code → display name
// - totalCountries / totalIslands
export function buildIndex(features) {
  const codeToCountry = {};
  const partsByCountry = {};
  const countryToName = {};
  const islandCenters = {};
  const featureCenters = {};
  let totalCountries = 0;
  let totalIslands = 0;
  for (const f of features) {
    const code = f.id;
    const country = f.properties.country || code;
    const partType = f.properties.partType || 'country';
    const individual = !!f.properties.individual;
    if (Number.isFinite(f.properties.lat) && Number.isFinite(f.properties.lng)) {
      featureCenters[code] = { lat: f.properties.lat, lng: f.properties.lng };
    } else if (f.geometry) {
      const { minLat, maxLat, minLng, maxLng } = computeBbox(f.geometry.coordinates);
      featureCenters[code] = { lat: (minLat + maxLat) / 2, lng: (minLng + maxLng) / 2 };
    }
    // Individual islands are self-contained "countries": their code maps to
    // itself (so records count separately) and they are counted + named as
    // countries. Mainland + unnamed islets stay grouped under the parent.
    if (individual) {
      codeToCountry[code] = code;
      totalIslands++;
      countryToName[code] = f.properties.name;
      if (f.properties.lat != null) islandCenters[code] = { lat: f.properties.lat, lng: f.properties.lng };
    } else {
      codeToCountry[code] = country;
      if (partType === 'country') {
        totalCountries++;
        countryToName[country] = f.properties.name;
      }
    }
    (partsByCountry[country] ||= []).push(code);
  }
  return { codeToCountry, partsByCountry, countryToName, totalCountries, totalIslands, islandCenters, featureCenters };
}

export async function fetchCountries() {
  const features = await loadCountryFeatures();
  const all = cleanFeatures(features)
    .filter((f) => f.properties.partType === 'country' || f.properties.individual)
    .map((f) => ({ code: f.id, name: f.properties.name, individual: !!f.properties.individual }))
    .sort((a, b) =>
      a.name.localeCompare(b.name) || (a.individual === b.individual ? 0 : a.individual ? 1 : -1)
    );
  // Deduplicate by name — splitParts emits one entry per island/islet, and
  // unnamed islets inherit the parent country's name, producing duplicates.
  const seen = new Set();
  return all
    .filter((c) => {
      if (seen.has(c.name)) return false;
      seen.add(c.name);
      return true;
    })
    .map(({ code, name }) => ({ code, name }));
}

export async function fetchCodeIndex() {
  const features = await loadCountryFeatures();
  return buildIndex(cleanFeatures(features));
}

let regionMapRefreshing = false;

function buildRegionMap(arr) {
  const map = {};
  arr.forEach((c) => {
    const code = c['alpha-3'];
    if (code) {
      map[code] = {
        region: c.region === 'Americas' ? 'America' : (c.region || 'Other'),
        subRegion: c['sub-region'] || 'Other',
      };
    }
  });
  Object.assign(map, REGION_OVERRIDES);
  return map;
}

function refreshRegionMap() {
  if (regionMapRefreshing) return;
  regionMapRefreshing = true;
  fetch(REGION_DATA_URL)
    .then((r) => r.json())
    .then((arr) => {
      try { localStorage.setItem('regionMap_v1', JSON.stringify(buildRegionMap(arr))); } catch {}
    })
    .catch(() => {})
    .finally(() => { regionMapRefreshing = false; });
}

export async function fetchRegionMap() {
  try {
    const cached = localStorage.getItem('regionMap_v1');
    if (cached) {
      refreshRegionMap();
      return JSON.parse(cached);
    }
  } catch {}

  try {
    const arr = await fetchJsonWithTimeout(REGION_DATA_URL, 8000);
    const map = buildRegionMap(arr);
    try { localStorage.setItem('regionMap_v1', JSON.stringify(map)); } catch {}
    return map;
  } catch {
    return {};
  }
}

// Add admin-1 land polygons that are missing from the country dataset, such
// as small offshore islands. A bbox overlap is not enough here: a mainland's
// bbox can contain many islands that are not part of its geometry.
export function orphanIslandFeatures(baseFeatures, regionFeatures) {
  const base = (baseFeatures || []).filter((feature) => feature.geometry);
  const result = [];
  const seen = new Set(base.map((feature) => feature.properties?.code || feature.id));
  for (const feature of regionFeatures || []) {
    if (!feature.geometry || !feature.properties) continue;
    const polygons = feature.geometry.type === 'Polygon'
      ? [feature.geometry.coordinates]
      : feature.geometry.type === 'MultiPolygon' ? feature.geometry.coordinates : [];
    polygons.forEach((polygon, index) => {
      const ring = polygon?.[0];
      if (!ring?.length) return;
      const box = computeBbox(ring);
      const lat = (box.minLat + box.maxLat) / 2;
      const lng = (box.minLng + box.maxLng) / 2;
      const samplePoints = [
        [lng, lat],
        ring[0],
        ring[Math.floor(ring.length / 3)],
        ring[Math.floor((ring.length * 2) / 3)],
      ];
      const covered = base.some((candidate) => samplePoints.some(([sampleLng, sampleLat]) => (
        pipGeometry(sampleLng, sampleLat, candidate.geometry)
      )));
      if (covered) return;
      const code = `${feature.properties.adm1_code}~${index}`;
      if (seen.has(code)) return;
      seen.add(code);
      result.push({
        type: 'Feature',
        id: code,
        properties: {
          code,
          name: feature.properties.name_en || feature.properties.name || feature.properties.NAME || 'Island',
          country: feature.properties.adm0_a3 || feature.properties.iso_a3,
          individual: true,
          partType: 'island',
          lat,
          lng,
        },
        geometry: { type: 'Polygon', coordinates: polygon },
      });
    });
  }
  return result;
}

// --- Admin-1 (states / provinces) regions ---
const ADMIN1_URL =
  'https://raw.githubusercontent.com/nvkelso/natural-earth-vector/master/geojson/ne_10m_admin_1_states_provinces.geojson';
const ADMIN1_URL_FALLBACK =
  'https://cdn.jsdelivr.net/gh/nvkelso/natural-earth-vector@master/geojson/ne_10m_admin_1_states_provinces.geojson';

async function fetchAdmin1FromNetwork() {
  const candidates = [ADMIN1_URL, ADMIN1_URL_FALLBACK];
  let lastError;

  for (const url of candidates) {
    try {
      return await fetchJsonWithTimeout(url, 8000);
    } catch (error) {
      lastError = error;
    }
  }

  throw lastError || new Error('Could not load admin-1 data');
}

// Raw 10m admin-1 GeoJSON for the region-border overlay shown at the closest zoom.
// Cached in IndexedDB (stale-while-revalidate) so zooming in is instant on
// repeat visits.
let admin1Refreshing = false;

function refreshAdmin1() {
  if (admin1Refreshing) return;
  admin1Refreshing = true;
  fetchAdmin1FromNetwork()
    .then((data) => {
      idbSet('ne_admin1_v3', data);
    })
    .catch(() => {})
    .finally(() => { admin1Refreshing = false; });
}

let admin1Promise = null;

function ensureAdmin1() {
  if (admin1Promise) return admin1Promise;
  admin1Promise = (async () => {
    const cached = await idbGet('ne_admin1_v3');
    if (cached) {
      refreshAdmin1();
      return cached;
    }
    const data = await fetchAdmin1FromNetwork();
    idbSet('ne_admin1_v3', data);
    return data;
  })();
  return admin1Promise;
}

export async function fetchAdmin1Regions() {
  return ensureAdmin1();
}

// --- Cache cleanup ---
// Prevents IndexedDB from accumulating stale cached data indefinitely.
async function cleanupOldCacheEntries() {
  try {
    const cachedMeta = await idbGet('cache_metadata') || {};
    const needsCleanup = cachedMeta.lastCleanup ? (Date.now() - cachedMeta.lastCleanup) > (7 * 24 * 60 * 60 * 1000) : true;
    if (!needsCleanup) return;
    await idbSet('cache_metadata', { lastCleanup: Date.now() });
  } catch {}
}

if (typeof window !== 'undefined') {
  setTimeout(() => cleanupOldCacheEntries(), 2000);
}

// One-shot signal resolved when WorldMap has loaded AND rendered the admin-1
// region layer. The initial loading screen awaits this (for users with marked
// regions) so the yellow region highlights don't pop in after the loader fades.
let _regionsReadyResolve;
export const regionsReadyPromise = new Promise((resolve) => {
  _regionsReadyResolve = resolve;
});
export function notifyRegionsReady() {
  _regionsReadyResolve?.();
  _regionsReadyResolve = null;
}

